import React, { useState, MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { CLOUDFRONT_ASSET_URL, INFO_SIGN } from '@osmosis/constants';
import { LinkObject, MenuTopic } from '../../types/topics';
import TopicsItemContainer from './TopicsItemContainer';
import { LabelComponent } from '../library/LabelComponent';
import { P, H4, A } from '@osmosis/tailwind-ui/components/Text';
import { text5, textBlue, textPrimary } from '@osmosis/tailwind-ui/styles/text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { useEmbedContext } from '@osmosis/nextjs-components/context/EmbedProvider';

type Props = {
  item: MenuTopic;
  hasBgImage?: boolean;
  dataTestId?: string;
  isMobileView?: boolean;
  onSelectTopic?: (topic: LinkObject) => void;
};

const TopicName = ({ name = '' }: { name: string }) => {
  const hasInfoSymbol = name.toLowerCase().includes(INFO_SIGN);

  const title = name.replace(new RegExp(INFO_SIGN, 'gi'), '');

  return (
    <>
      <H4 className={`inline min-w-fit text-sm ${textPrimary}`}>{title}</H4>
      {hasInfoSymbol ? (
        <span
          data-testid="topics_L1_info_icon"
          className={`hover:text-primary-700 cursor-pointer pl-1.5 text-sm`}
        >
          {INFO_SIGN}
        </span>
      ) : null}
    </>
  );
};

/**
 * TopicsItem.
 */
const TopicsItem = (props: Props) => {
  const {
    item,
    hasBgImage,
    dataTestId,
    isMobileView = false,
    onSelectTopic,
  } = props;

  const [maxLength, setMaxLength] = useState(13);

  const handleMoreClick = async (e: React.MouseEvent, path: string) => {
    e.stopPropagation();
    await setMaxLength(99);

    const element = document.getElementById(`${path}_13`);
    if (element) {
      element.focus();
    }
  };

  const isButton = !!onSelectTopic;
  const { targetAttribute } = useEmbedContext();

  return (
    <TopicsItemContainer
      isMobileView={isMobileView}
      displayName={item.displayName}
    >
      <Box data-testid="topics-item" className={`relative mx-4 w-full`}>
        {!isMobileView && (
          <Flex
            data-testid={`${dataTestId}_title`}
            className={`flex-nowrap items-center justify-start px-2 py-4`}
          >
            <TopicName name={item.displayName} />
          </Flex>
        )}

        {item.links?.slice(0, maxLength).map((el, idx) => {
          const onClickHandler = (e: MouseEvent) => {
            if (onSelectTopic) {
              e.preventDefault();
              e.stopPropagation();
              onSelectTopic(el);
            }
          };

          return (
            <React.Fragment key={el.link}>
              {el.type === 'tile' ? (
                <Flex
                  className={`m-2 w-[90%] items-center justify-center rounded-md hover:cursor-pointer hover:bg-blue-200 hover:transition-all`}
                >
                  <P
                    data-testid="more-topics-coming-soon"
                    className={`hover:text-primary-700 p-2 text-xs font-bold hover:cursor-pointer`}
                  >
                    {el.name}
                  </P>
                </Flex>
              ) : (
                <Box
                  className={`break-words rounded-[5px] duration-300 hover:bg-blue-100 hover:no-underline hover:transition-all`}
                >
                  <A
                    data-testid={`header_topic_${el.link}`}
                    onClick={isButton ? onClickHandler : void 0}
                    id={`${item.path}_${idx}`}
                    key={el.link}
                    href={isButton ? void 0 : el.link}
                    target={targetAttribute}
                    aria-label={el.name}
                    className={`${text5} hover:text-primary-700 w-full text-left no-underline `}
                  >
                    <Box
                      className={`min-w-[auto] rounded-md px-2 py-2 hover:bg-sky-600/25 lg:min-w-[100px]`}
                    >
                      <Flex className={`items-center justify-between`}>
                        <Box className={`min-w-4/5 mr-2 text-sm`}>
                          {el.name}
                        </Box>

                        {el.label === 'new' && (
                          <LabelComponent
                            as={isButton ? 'div' : 'button'}
                            content={
                              <FormattedMessage
                                defaultMessage="NEW"
                                id="library.topics.list.item.tag.new"
                              />
                            }
                            dataTestId={`new-tag-${idx}`}
                            containerProps={{ style: { minWidth: '10%' } }}
                            tabIndex={-1}
                          />
                        )}
                      </Flex>
                    </Box>
                  </A>
                </Box>
              )}
            </React.Fragment>
          );
        })}
        {item.links.length >= 13 && (
          <Button
            data-testid="more-btn"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              handleMoreClick(e, item.path);
            }}
            className={`cursor-pointer bg-transparent p-2 text-base font-normal ${textBlue} hover:bg-transparent${
              item.links.length > maxLength ? 'block' : 'hidden'
            }`}
          >
            <FormattedMessage
              id="components.topics-item.more"
              defaultMessage="More..."
            />
          </Button>
        )}

        {hasBgImage && (
          <Box
            style={{
              background: `url(${CLOUDFRONT_ASSET_URL}9zlkeqWdRFuHt4zJXV9w64OqQqKETORf.png) no-repeat right bottom`,
              backgroundSize: 'contain',
            }}
            className={`absolute right-10 top-[-49px] h-10 w-[50px]`}
          ></Box>
        )}
      </Box>
    </TopicsItemContainer>
  );
};

export default TopicsItem;
